/** @format */
import React, { useState } from 'react';
import Card from '../../components/ui/card';
import Button from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import RenewalCalendarIcon from '../../components/ui/icons/RenewalCalendarIcon';
import RenewalManagementModal from './RenewalManagementModal';
import { FiCalendar, FiArrowRight, FiAlertTriangle } from 'react-icons/fi';

// Constants for renewal urgency thresholds
const RENEWAL_THRESHOLDS = {
	CRITICAL_COUNT: 5,
};

const RenewalRadarCard = ({ 
	urgentRenewals = 0, 
	upcomingRenewals = 0, 
	urgentRenewalsList = [],
	upcomingRenewalsList = [],
	isLoading = false 
}) => {
	const [showRenewalModal, setShowRenewalModal] = useState(false);
	// Calculate total renewals in 30-day window
	const totalRenewals = urgentRenewals + upcomingRenewals;

	// Get preview of next renewals (combine urgent and upcoming, sorted by urgency)
	const previewRenewals = [
		...urgentRenewalsList,
		...upcomingRenewalsList
	]
	.sort((a, b) => a.daysUntilRenewal - b.daysUntilRenewal)
	.slice(0, 2) // Show only first 2 for preview
	.map(renewal => ({
		name: renewal.customerName,
		days: renewal.daysUntilRenewal,
		amount: Math.round(renewal.amount / 100) // Convert from cents to dollars
	}));

	if (isLoading) {
		return (
			<Card className="overflow-hidden animate-pulse">
				<div className="p-4 sm:p-6">
					<div className="flex justify-between items-center mb-4">
						<div className="h-4 bg-gray-200 rounded w-24"></div>
						<div className="rounded-full p-3 sm:p-4 bg-gray-200">
							<div className="h-6 w-6 sm:h-7 sm:w-7 bg-gray-300 rounded"></div>
						</div>
					</div>
					<div className="space-y-3">
						<div className="flex justify-between items-center">
							<div className="h-4 bg-gray-200 rounded w-16"></div>
							<div className="h-6 bg-gray-200 rounded w-16"></div>
						</div>
						<div className="flex justify-between items-center">
							<div className="h-4 bg-gray-200 rounded w-16"></div>
							<div className="h-6 bg-gray-200 rounded w-16"></div>
						</div>
					</div>
					<div className="h-8 bg-gray-200 rounded w-full mt-4"></div>
				</div>
			</Card>
		);
	}

	return (
		<>
		<Card className="overflow-hidden bg-white border border-gray-100">
			<div className="p-4 sm:p-6">
				{/* Header Section */}
				<div className="flex justify-between items-center mb-6">
					<h3 className="text-sm font-montserrat-medium text-gray-700 uppercase tracking-wide">
						Renewal Radar
					</h3>
					<div className="rounded-full p-3 sm:p-4 bg-gray-100">
						<RenewalCalendarIcon
							className="h-6 w-6 sm:h-7 sm:w-7 text-clientBlack"
							urgentCount={urgentRenewals}
							upcomingCount={upcomingRenewals}
							showPinkAccent={urgentRenewals > RENEWAL_THRESHOLDS.CRITICAL_COUNT}
						/>
					</div>
				</div>

				{/* Total Renewals Display */}
				<div className="mb-6">
					<p className="text-2xl sm:text-3xl font-glacial text-gray-900">
						{totalRenewals}
					</p>
					<p className="text-sm font-montserrat-regular text-gray-500">
						renewals in next 30 days
					</p>
				</div>

				{/* Urgency Breakdown */}
				<div className="space-y-4 mb-6">
					{/* Critical - Next 7 days */}
					<div className="flex justify-between items-center">
						<div className="flex items-center space-x-2">
							<FiAlertTriangle className="h-4 w-4 text-primary" />
							<span className="text-sm font-montserrat-regular text-gray-600">
								Next 7 days
							</span>
						</div>
						<Badge
							variant="secondary"
							className="font-montserrat-medium bg-primary/10 text-primary border-primary/20"
						>
							{urgentRenewals} critical
						</Badge>
					</div>

					{/* Upcoming - 8-30 days */}
					<div className="flex justify-between items-center">
						<div className="flex items-center space-x-2">
							<FiCalendar className="h-4 w-4 text-gray-500" />
							<span className="text-sm font-montserrat-regular text-gray-600">
								8-30 days
							</span>
						</div>
						<Badge
							variant="secondary"
							className="font-montserrat-medium bg-gray-100 text-gray-700 border-gray-200"
						>
							{upcomingRenewals} upcoming
						</Badge>
					</div>
				</div>

				{/* Preview List (if renewals exist) */}
				{totalRenewals > 0 && (
					<div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
						<div className="text-xs font-montserrat-medium text-gray-600 mb-2 uppercase tracking-wide">
							Next Renewals
						</div>
						<div className="space-y-2">
							{previewRenewals.length > 0 ? previewRenewals.map((renewal, index) => (
								<div key={index} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 text-xs">
									<span className="font-montserrat-regular text-gray-700 truncate">
										{renewal.name}
									</span>
									<div className="flex items-center space-x-2 flex-shrink-0">
										<span className={`font-montserrat-medium ${
											renewal.days <= 7 ? 'text-primary' : 'text-gray-600'
										}`}>
											{renewal.days}d
										</span>
										<span className="font-montserrat-regular text-gray-500">
											${renewal.amount}
										</span>
									</div>
								</div>
							)) : (
								<div className="text-xs text-gray-500 font-montserrat-regular">
									No upcoming renewals
								</div>
							)}
						</div>
					</div>
				)}

				{/* Action Button */}
				<Button
					variant="outline"
					size="sm"
					className="w-full font-montserrat-medium group min-h-[44px] sm:min-h-0"
					disabled={totalRenewals === 0}
					onClick={() => totalRenewals > 0 && setShowRenewalModal(true)}
				>
					{totalRenewals > 0 ? 'Manage Renewals' : 'No Renewals Pending'}
					{totalRenewals > 0 && (
						<FiArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
					)}
				</Button>

				{/* Footer Info */}
				<div className="mt-4 pt-4 border-t border-gray-100">
					<div className="flex justify-between items-center text-xs">
						<span className="font-montserrat-regular text-gray-500">
							Updated: {new Date().toLocaleDateString()}
						</span>
						<span className="font-montserrat-medium text-gray-600">
							Proactive retention management
						</span>
					</div>
				</div>
			</div>
		</Card>

		{/* Renewal Management Modal */}
		<RenewalManagementModal 
			isOpen={showRenewalModal}
			onClose={() => setShowRenewalModal(false)}
			urgentRenewalsList={urgentRenewalsList}
			upcomingRenewalsList={upcomingRenewalsList}
		/>
	</>
	);
};

export default RenewalRadarCard;